import { AnyStepData, Step, StepGroup, StepSelections, StepType, Workflow } from "../../types";

/**
 * A renderable scene is a scene that can be displayed to the user. This is based on the workflow state.
 */
export interface RenderableScene {
    /**
     * The id of the scene.
     */
    id: string;
    /**
     * The title of the scene.
     */
    title: string;
    /**
     * The id of each step inside the scene that can be rendered. This is based on the workflow state.
     */
    renderableSteps: string[];
    /**
     * The WorkflowScene representation of this object. Provided for backwards compatibility.
     * @deprecated
     */
    workflowScene: WorkflowScene;
}

/**
 * Information parsed froma workflow structure that is relevant
 * to a given scene.
 */
export interface WorkflowScene {
    /**
     * A unique identifier for the scene.
     */
    name: string;
    /**
     * A human-readable title for the scene.
     */
    title: string;
    /**
     * Steps which can display to the user.
     * But may be conditionally hidden based on workflow logic
     */
    renderableSteps: Step<AnyStepData>[];
    /**
     * Steps which don't display to the user.
     * Their behavior is always silent & executed in the background.
     * @deprecated Silent steps are no longer handled seperately from normal steps.
     */
    silentSteps: Step<AnyStepData>[];
}

/**
 * @deprecated All steps can now be silent. It will take a long time to transition away from this approach though. Including potentially more dev work.
 */
export const silentStepTypes = [StepType.SilentIllustration, StepType.ProductOverlay];

/**
 * Gets all scenes in the workflow structured in a way that makes it easier for us to track current state.
 * FIXME: renderableSteps in the response from this function have a different meaning to renderableSteps in getRenderableRelevantScenes and others.
 * FIXME: silentSteps will be removed in the future. They will be treating like other steps & not a special case moving forwards.
 * @param workflow The workflow to get scenes for.
 * @returns A list of scenes containing
 */
const getAllScenes = async (workflow: Workflow): Promise<WorkflowScene[]> => {
    const scenes: WorkflowScene[] = [];

    // If the workflow needs an intro, add it.
    if (workflow.introduction) {
        scenes.push({
            name: "Introduction",
            title: workflow.name,
            renderableSteps: [
                {
                    type: StepType.Introduction,
                    stepName: "Introduction",
                    stepTitle: workflow.name,
                    helpText: workflow.introduction,
                    data: {},
                    conditions: [],
                },
            ],
            silentSteps: [],
        });
    }

    // For each step in the workflow,
    // check if it belongs to a group.
    // If not then stick it in its own scene.
    // If it does then look for a scene with the group's name.
    // Stick it in that scene if you find it, otherwise make a new scene.
    for (const step of workflow.steps) {
        const group = findGroupForStep(step.stepName, workflow.stepGroups);

        // FIXME: I don't believe the system actually allows you to have groupless steps anymore...
        if (!group) {
            scenes.push({
                name: step.stepName,
                title: step.stepTitle,
                renderableSteps: silentStepTypes.includes(step.type) ? [] : [step],
                silentSteps: silentStepTypes.includes(step.type) ? [step] : [],
            });
        } else {
            const existingScene = scenes.find((scene) => scene.name === group.name);
            if (existingScene) {
                if (silentStepTypes.includes(step.type)) {
                    existingScene.silentSteps.push(step);
                } else {
                    existingScene.renderableSteps.push(step);
                }
            } else {
                scenes.push({
                    name: group.name,
                    title: group.name,
                    renderableSteps: silentStepTypes.includes(step.type) ? [] : [step],
                    silentSteps: silentStepTypes.includes(step.type) ? [step] : [],
                });
            }
        }
    }

    // Add the finish step.
    const finishSceneTitle = "workflow.steps.finish.confirmDesign";
    scenes.push({
        name: "Finish",
        title: finishSceneTitle,
        renderableSteps: [
            {
                type: StepType.Finish,
                stepName: "Finish",
                stepTitle: finishSceneTitle,
                helpText: "",
                data: {
                    modelAnimation: workflow.finalizeStepConfig
                        ? workflow.finalizeStepConfig.modelAnimation
                        : undefined,
                    lookAtAnimation: workflow.finalizeStepConfig
                        ? workflow.finalizeStepConfig.lookAtAnimation
                        : undefined,
                },
                conditions: [],
            },
        ],
        silentSteps: [],
    });

    return scenes;
};

/**
 * Find the scene relating to a step
 * @param stepName The step ID.
 * @param stepGroups The list of scenes.
 * @returns Scene if found or undefined.
 */
const findGroupForStep = (stepName: string, stepGroups: StepGroup[]): StepGroup | undefined => {
    return stepGroups.find((group) => group.stepNames.includes(stepName));
};

/**
 * Determines whether a step is conditionally active.
 * @param step A step to check.
 * @param stepSelections The current customer selections.
 * @returns Returns true when the step is conditionally active. False otherwise.
 */
const stepConditionsAreSatisfied = (step: Step<AnyStepData>, stepSelections: StepSelections): boolean => {
    // Currently all conditions must be met (AND), we should allow for OR of conditions as well.
    return (step.conditions || []).every((condition) => {
        const originatingStepData = stepSelections[condition.targetStepName];
        if (originatingStepData && originatingStepData.selectedVariants) {
            const selectedVariants = originatingStepData.selectedVariants;
            return condition.requiredVariantSelections.some(
                (reqVariant) => selectedVariants.find((selVariant) => selVariant.id === reqVariant) !== undefined,
            );
        }
        return false;
    });
};

/**
 * Filters out steps that are not conditionally active.
 * @param scene The scene to filter.
 * @param stepSelections The current customer selections.
 * @returns A scene with only conditionally active steps. If all steps have been filtered out we return null.
 */
const filterSceneByConditions = (scene: WorkflowScene, stepSelections: StepSelections): WorkflowScene | null => {
    const filteredScene: WorkflowScene = {
        name: scene.name,
        title: scene.title,
        renderableSteps: scene.renderableSteps.filter((step) => stepConditionsAreSatisfied(step, stepSelections)),
        silentSteps: scene.silentSteps.filter((step) => stepConditionsAreSatisfied(step, stepSelections)),
    };
    if (filteredScene.silentSteps.length === 0 && filteredScene.renderableSteps.length === 0) {
        return null;
    }
    return filteredScene;
};

/**
 * Used to understand what scenes contain active steps.
 * @param allScenes All scenes in the workflow.
 * @param stepSelections The current customer selections.
 * @returns A list of scenes that contain at least one conditionally active step.
 */
const getActiveScenes = (allScenes: WorkflowScene[], stepSelections: StepSelections): WorkflowScene[] => {
    return allScenes
        .map((scene) => filterSceneByConditions(scene, stepSelections))
        .filter((scene) => scene !== null) as WorkflowScene[];
};

/**
 * This function is only used to drive what steps are visible for the user.
 * NOTE: Do not use this function to apply internal workflow logic like loading/unloading steps. As a renderable step may still need to be loaded/unloaded.
 * @param allScenes All scenes in the workflow.
 * @param stepSelections The current customer selections.
 * @param singleVariantsRenderable When true the function will return steps that have a single variant.
 * @returns A list of scenes & steps inside them that are renderable to the customer.
 */
const getRenderableRelevantScenes = async (
    allScenes: WorkflowScene[],
    stepSelections: StepSelections,
    singleVariantsRenderable: boolean = false,
): Promise<WorkflowScene[]> => {
    const activeScenes = getActiveScenes(allScenes, stepSelections);

    // Evaluate each step
    const stepsWithRenderableConfiguration: string[] = [];
    for (const scene of activeScenes) {
        for (const step of scene.renderableSteps) {
            const variants = (step.option?.variants || []).length;
            if (step.silent) continue;
            /**
             * Silent steps are now possible on any step in the system.
             * Let's move towards removing this code.
             * FIXME: Once ops are happy we have marked required steps silent. Remove.
             */
            if (
                step.type === StepType.Model ||
                step.type === StepType.Material ||
                step.type === StepType.Picture ||
                step.type === StepType.Shape
            ) {
                if (variants > 1 || singleVariantsRenderable) stepsWithRenderableConfiguration.push(step.stepName);
            } else {
                stepsWithRenderableConfiguration.push(step.stepName);
            }
        }
    }
    // Filter out scenes that don't have any renderable steps.
    const renderableScenes = activeScenes.filter((scene) => {
        const filteredScene = scene.renderableSteps.filter((step: Step<AnyStepData>) =>
            stepsWithRenderableConfiguration.includes(step.stepName),
        );
        return filteredScene.length > 0;
    });

    // Filter out unrenderable steps from the scenes.
    for (const renderableScene of renderableScenes) {
        renderableScene.renderableSteps = renderableScene.renderableSteps.filter((step: Step<AnyStepData>) =>
            stepsWithRenderableConfiguration.includes(step.stepName),
        );
    }

    return renderableScenes;
};

export { getAllScenes, getActiveScenes, getRenderableRelevantScenes, stepConditionsAreSatisfied };
